// Client-side API layer for the Wealthbox→Slant switch widget (APP-1303).
//
// STATELESS contract — base `slant/migration` (firm/auth-scoped). There is no
// server-side state machine, no `slant_migration_state`, no per-step persistence.
// The wizard holds ALL its own state in React and calls these reads plus a single
// final `switch` mutation:
//
//   useHouseholdPeople   GET  households/:id/people
//        → { people: [{ contact_id, name, email?, phone? }] }
//   useSlantCandidates   GET  households/:id/slant-candidates?name&email&phone
//        → { candidates: [{ id, name, book_id?, ratio }] }
//   useSlantPeople       GET  households/:id/slant-people?slantHouseholdId
//        → { people: [{ id, name, email? }], suggestions: [{ contact_id, slant_person_id, auto }] }
//   useSwitchToSlant     POST households/:id/switch
//        body { slantHouseholdId, decisions: [{ contact_id, slant_person_id }] }
//        → { people: <N>, household_repointed: true, slant_household_id }
//
// Responses are `{ data: <payload> }`-wrapped per the API's `wrapResponse`
// convention (every other read hook in this client unwraps `.data`).
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  serverError,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../../lib/api";

/**
 * Whether a failed wizard request was answered with HTTP 429 (APP-1678).
 * @param error The value caught from a failed request
 */
function isTooManyRequests(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 429;
}

/**
 * The text a wizard step shows in its danger alert for a failed request
 * (APP-1678). On HTTP 429 that is the `message` of the api's error body,
 * whatever it says. The case that motivated this is the Slant rate limiter's
 * `SLANT_RATE_LIMIT_MESSAGE` (`api/src/slant/rate-limiter.ts`), which tells the
 * advisor to wait rather than suspect the setup. It is not the only one: the
 * api's global `ThrottlerGuard` (`api/src/app/app.module.ts`) also answers 429,
 * with its own message, and that is shown as it is. Every other status, and a
 * 429 with no recoverable `message`, shows the step's fixed string.
 * @param error The error of the step's failed query or mutation
 * @param fixedMessage The string the step shows for any other failure
 */
export function stepErrorMessage(error: unknown, fixedMessage: string): string {
  if (!isTooManyRequests(error)) {
    return fixedMessage;
  }
  // `serverError` casts the parsed body, so `message` is checked, not trusted.
  const message: unknown = serverError(error)?.body?.message;
  return typeof message === "string" && message.trim() !== ""
    ? message
    : fixedMessage;
}

/** The other household that a "Household already mapped" refusal names. */
export type MappedHousehold = {
  /** The AdviceCloud household id, for the link to the household's page. */
  id: number;
  /** The household's name, or `null` when it has none. */
  name: string | null;
};

/**
 * Whether the api refused the switch because the chosen Slant household is
 * already linked to a different AdviceCloud household (APP-1690), and which
 * household that is. `null` for every other failure.
 *
 * The api answers that case with a 409 whose `details` carry
 * `householdAlreadyMapped` (`HouseholdAlreadyMappedDetails`,
 * `api/src/slant/migration.service.ts`). The test is `=== true`, as in
 * `slantTokenRenewal` (`clients/household/HouseholdCashBilling.tsx`): only the
 * route that means the discriminator sets it. `household` is `null` when the
 * api names no household, which is the case when the other household is in
 * another firm.
 * @param error The error of the failed `switch` mutation
 */
export function householdAlreadyMapped(
  error: unknown,
): { household: MappedHousehold | null } | null {
  const details = serverError(error)?.body?.details;
  if (details?.householdAlreadyMapped !== true) {
    return null;
  }
  // `serverError` casts the parsed body, so each field is checked, not trusted.
  const mapped: unknown = details.mappedHousehold;
  if (typeof mapped !== "object" || mapped === null) {
    return { household: null };
  }
  const { id, name } = mapped as { id?: unknown; name?: unknown };
  if (typeof id !== "number" || !Number.isInteger(id)) {
    return { household: null };
  }
  return {
    household: {
      id,
      name: typeof name === "string" && name.trim() !== "" ? name : null,
    },
  };
}

/**
 * React Query `retry` for the wizard's reads (Steps 1 to 3). A 429 tells the
 * advisor to wait, so it is not retried; the React Query default of three
 * retries with backoff held the message back for seconds and sent three more
 * requests. Every other failure keeps that default.
 * @param failureCount How many times the query has failed so far
 * @param error The value the last attempt failed with
 */
export function retryUnlessTooManyRequests(
  failureCount: number,
  error: unknown,
): boolean {
  return !isTooManyRequests(error) && failureCount < 3;
}

/** A person on the household (the source side of the switch). */
export type HouseholdPerson = {
  /** The AC contact id, as returned by `people`/`suggestions`. */
  contact_id: number;
  name: string;
  email?: string;
  phone?: string;
};

/** A ranked Slant household candidate (`slant-candidates`), best-first. */
export type SlantCandidate = {
  id: string;
  name: string;
  book_id?: string;
  /** Fuzzy match ratio against the search terms (higher = stronger). */
  ratio: number;
};

/** A Slant person on the chosen Slant household (`slant-people`). */
export type SlantPerson = {
  id: string;
  name: string;
  email?: string;
};

/**
 * A server-proposed AC-contact → Slant-person link (`slant-people`).
 * `auto` marks an exact match the server is confident about; the wizard
 * pre-fills auto rows and lets the advisor confirm/override the rest.
 */
export type SlantPersonSuggestion = {
  contact_id: number;
  slant_person_id: string;
  auto: boolean;
};

/** The `slant-people` payload: Slant people plus proposed links. */
export type SlantPeopleResult = {
  people: SlantPerson[];
  suggestions: SlantPersonSuggestion[];
};

/** A single advisor-confirmed link, posted to `switch`. */
export type SwitchDecision = {
  contact_id: number;
  slant_person_id: string;
};

/** The `switch` request body. */
export type SwitchRequest = {
  slantHouseholdId: string;
  decisions: SwitchDecision[];
};

/** The `switch` result, rendered as the Step 4 success summary. */
export type SwitchResult = {
  people: number;
  household_repointed: boolean;
  slant_household_id: string;
};

type Wrapped<T> = { data: T };

const base = (householdId: number) =>
  `/slant/migration/households/${householdId}`;

/**
 * Read the people on the household (Step 1), straight from AdviceCloud. Always
 * enabled — this is the source manifest the advisor confirms before searching
 * Slant. The query key stays the default, `[url]`.
 */
export function useHouseholdPeople(householdId: number) {
  return useAuthenticatedFetch<Wrapped<{ people: HouseholdPerson[] }>>(
    `${base(householdId)}/people`,
    {},
    { retry: retryUnlessTooManyRequests },
  );
}

/**
 * Search Slant for candidate households (Step 2). Disabled until at least one
 * search term is supplied; keyed on the terms so each distinct search caches
 * independently. The advisor drives the terms from Step 1's manifest.
 */
export function useSlantCandidates(
  householdId: number,
  terms: { name?: string; email?: string; phone?: string },
) {
  const params = new URLSearchParams();
  if (terms.name) params.set("name", terms.name);
  if (terms.email) params.set("email", terms.email);
  if (terms.phone) params.set("phone", terms.phone);
  const query = params.toString();
  const url = `${base(householdId)}/slant-candidates${query ? `?${query}` : ""}`;

  return useAuthenticatedFetch<Wrapped<{ candidates: SlantCandidate[] }>>(
    url,
    {},
    { enabled: query.length > 0, retry: retryUnlessTooManyRequests },
    [url],
  );
}

/**
 * Read the Slant people on the chosen Slant household plus the server's proposed
 * person→person links (Step 3). Disabled until a Slant household is chosen;
 * keyed on that id so switching the chosen household refetches.
 */
export function useSlantPeople(
  householdId: number,
  slantHouseholdId: string | null,
) {
  const url = `${base(householdId)}/slant-people?slantHouseholdId=${encodeURIComponent(
    slantHouseholdId ?? "",
  )}`;

  return useAuthenticatedFetch<Wrapped<SlantPeopleResult>>(
    url,
    {},
    {
      enabled: slantHouseholdId !== null && slantHouseholdId !== "",
      retry: retryUnlessTooManyRequests,
    },
    [url],
  );
}

/**
 * The single final mutation (Step 4): link the confirmed people and re-point the
 * household to Slant in one server call. Stateless — a retry just re-POSTs the
 * same body; there is no server-side row to resume.
 */
export function useSwitchToSlant(householdId: number) {
  const queryClient = useQueryClient();
  const doSwitch = useAuthenticatedMutationAsync<Wrapped<SwitchResult>>(
    `${base(householdId)}/switch`,
    async (body: SwitchRequest) => ({
      method: "POST",
      body: JSON.stringify(body),
    }),
  );

  return useMutation({
    mutationFn: (body: SwitchRequest) => doSwitch(body),
    // The switch re-points the household from Wealthbox to Slant, so the
    // surrounding page's household/`idExternal` caches are now stale. Invalidate
    // them (per the CRM-mapping convention in HouseholdMapping.tsx) so the page
    // refreshes to the Slant-linked record instead of the old Wealthbox one.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["households"] });
    },
  });
}
